/**
 * これまでの会話（30 日分）
 *
 * 日ごとにまとめて新しい順に出す（GET /api/chat/conversations）。押すとその会話を開く。
 * 題は最初の依頼の冒頭（用途なら「連絡文を作る: ◯◯・お礼・LINE」のような文）。
 */

import { useQuery } from '@tanstack/react-query';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { CHAT_RETENTION_DAYS } from '@/constants/chat';
import { useAuth } from '@/hooks/useAuth';
import { chatQueryKeys } from '@/queryKeys/chat';
import { listChatConversations, type ChatConversationSummary } from '@/services/chat';

const WEEKDAYS = '日月火水木金土';

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function dayLabel(date: Date, now: Date): string {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(date) === dayKey(now)) return '今日';
  if (dayKey(date) === dayKey(yesterday)) return '昨日';
  return `${date.getMonth() + 1}/${date.getDate()}（${WEEKDAYS[date.getDay()]}）`;
}

function timeLabel(date: Date): string {
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function groupByDay(rows: ChatConversationSummary[], now: Date) {
  const groups: { label: string; rows: { row: ChatConversationSummary; time: string }[] }[] = [];
  for (const row of rows) {
    const date = new Date(row.updatedAt);
    if (Number.isNaN(date.getTime())) continue;
    const label = dayLabel(date, now);
    const last = groups[groups.length - 1];
    const item = { row, time: timeLabel(date) };
    if (last && last.label === label) last.rows.push(item);
    else groups.push({ label, rows: [item] });
  }
  return groups;
}

export default function ChatHistory({ onOpen }: { onOpen: (conversationId: string) => void }) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: chatQueryKeys.conversations(user?.id ?? ''),
    queryFn: listChatConversations,
    // 開くたびに読み直す（アプリ全体の既定は長めにキャッシュするため、さっきの会話が出ない）
    staleTime: 0,
    refetchOnMount: 'always',
  });

  if (query.isLoading) {
    return (
      <p className="text-sm text-text-500" aria-live="polite">
        読み込んでいます…
      </p>
    );
  }
  if (query.isError) {
    return (
      <p className="text-sm text-text-500">読み込めませんでした。閉じて開き直してください。</p>
    );
  }
  const groups = groupByDay(query.data ?? [], new Date());

  return (
    <div className="space-y-4">
      {groups.length === 0 && (
        <p className="text-sm text-text-600">{CHAT_RETENTION_DAYS}日以内の会話はありません。</p>
      )}
      {groups.map((group) => (
        <section key={group.label} className="space-y-1">
          <h3 className="text-[11px] font-medium tracking-wide text-text-500">{group.label}</h3>
          <ul className="divide-y divide-dashed divide-text-100">
            {group.rows.map(({ row, time }) => (
              <li key={row.conversationId}>
                <button
                  type="button"
                  onClick={() => onOpen(row.conversationId)}
                  className="flex w-full items-center gap-2 py-2.5 text-left hover:text-primary-700"
                >
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-sm text-text-900">{row.title}</span>
                    <span className="block text-[11px] text-text-500">
                      {/* 題が用途の名前そのもの（題を持つ前の会話）なら、名前を繰り返さない */}
                      {row.title === row.label ? time : `${time}・${row.label}`}
                    </span>
                  </span>
                  <ChevronRightIcon className="h-4 w-4 flex-none text-text-400" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p className="text-[11px] text-text-400">
        会話は最後に使ってから{CHAT_RETENTION_DAYS}日で自動で消えます。
      </p>
    </div>
  );
}
